import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/Icon.tsx';
import { useFilterStore } from '../stores/filter-store.ts';

type IconName =
  | 'dashboard'
  | 'table'
  | 'reports'
  | 'trendingUp'
  | 'trendingDown'
  | 'star'
  | 'clock';

interface ReportCard {
  id: string;
  title: string;
  description: string;
  icon: IconName;
  /** Wo gehts hin (Path relativ zum SPA-Basename `/statistik/`). */
  to: string;
  /** Filter-Preset, das vor der Navigation gesetzt wird. */
  preset?: Record<string, (string | number)[] | { min?: string; max?: string }>;
  /** Wenn true: greyed-out Card, kein Click. */
  comingSoon?: boolean;
  category: 'Standard' | 'Vergleich' | 'Spezialisiert' | 'Tabellen';
}

function lastNDays(n: number): { min: string; max: string } {
  const max = new Date();
  const min = new Date();
  min.setDate(min.getDate() - (n - 1));
  return {
    min: min.toISOString().substring(0, 10),
    max: max.toISOString().substring(0, 10),
  };
}

const REPORTS: ReportCard[] = [
  // ── Standard ──────────────────────────────────────────────
  {
    id: 'artikel-analyse',
    title: 'Artikel-Analyse',
    description:
      'Einen oder mehrere Artikel suchen (Bezeichnung, EAN/PLU, Mono-Nr, DRS-Nr) → Umsatz/Menge, Monats-Verlauf und Top-Filialen.',
    icon: 'table',
    to: '/reports/artikel',
    category: 'Standard',
  },
  {
    id: 'wg-tagesumsatz',
    title: 'WG-Tagesumsatz',
    description:
      'Klassischer Tages-Bericht pro Filiale: Datum × Warengruppe Matrix mit Wochen-Subsummen und Monats-Summary (Brutto / Netto / 7% / 19%).',
    icon: 'table',
    to: '/reports/wg-tagesumsatz',
    category: 'Standard',
  },
  {
    id: 'umsatz',
    title: 'Umsatz-Übersicht',
    description: 'Gesamtumsatz, Bons, Ø-Bon, Storno-Quote — KPI-Karten und Zeitverlauf.',
    icon: 'trendingUp',
    to: '/reports/umsatz',
    category: 'Standard',
  },
  {
    id: 'heatmap',
    title: 'Stoßzeiten-Heatmap',
    description: 'Wochentag × Stunde — wann ist am meisten los?',
    icon: 'dashboard',
    to: '/reports/heatmap',
    category: 'Standard',
  },
  {
    id: 'storno-quote',
    title: 'Storno-Analyse',
    description: 'Storno-Quote pro Filiale und Kassierer — farbcodiert.',
    icon: 'trendingDown',
    to: '/reports/stornos',
    category: 'Standard',
  },

  // ── Tabellen / TopN ──────────────────────────────────────
  {
    id: 'top100-artikel',
    title: 'TOP 100 Artikel',
    description: 'Bestverkäufe nach Umsatz oder Menge. Beliebig filterbar, exportierbar.',
    icon: 'table',
    to: '/reports/topn',
    preset: { /* groupBy=artikel kommt aus URL */ },
    category: 'Tabellen',
  },
  {
    id: 'top-warengruppen',
    title: 'Warengruppen-Ranking',
    description: 'Top-Warengruppen mit Umsatzanteil pro Filiale.',
    icon: 'table',
    to: '/reports/topn?dim=warengruppe_name',
    category: 'Tabellen',
  },
  {
    id: 'top-kassierer',
    title: 'Kassierer-Performance',
    description: 'Ranking nach Umsatz, mit Ø-Bon-Berechnung.',
    icon: 'table',
    to: '/reports/topn?dim=kassierer_name',
    category: 'Tabellen',
  },
  {
    id: 'wg-tabelle-monat',
    title: 'Warengruppen × Monat',
    description: 'Pivot mit Warengruppen als Zeilen, Monate als Spalten — perfekt für PDF.',
    icon: 'table',
    to: '/pivot?preset=wg-monat',
    category: 'Tabellen',
  },
  {
    id: 'filial-tabelle',
    title: 'Filialen × Warengruppe',
    description: 'Welche Warengruppen laufen in welcher Filiale am besten?',
    icon: 'table',
    to: '/pivot?preset=filial-wg',
    category: 'Tabellen',
  },

  // ── Vergleiche ───────────────────────────────────────────
  {
    id: 'periodenvergleich',
    title: 'Periodenvergleich',
    description: 'Zwei beliebige Zeiträume parallel — Delta absolut + prozentual.',
    icon: 'trendingUp',
    to: '/periodenvergleich',
    category: 'Vergleich',
  },
  {
    id: 'wochenvergleich',
    title: 'Wochenvergleich',
    description: 'Diese Woche vs. letzte Woche, gruppiert nach Filiale.',
    icon: 'trendingUp',
    to: '/periodenvergleich?preset=woche',
    category: 'Vergleich',
  },
  {
    id: 'monatsvergleich',
    title: 'Monatsvergleich',
    description: 'Aktueller Monat vs. Vormonat — pro Warengruppe.',
    icon: 'trendingUp',
    to: '/periodenvergleich?preset=monat',
    category: 'Vergleich',
  },
  {
    id: 'wg-trend',
    title: 'Warengruppen-Trend',
    description: 'Eine Warengruppe über die Zeit — Trendlinie mit Vorjahresvergleich.',
    icon: 'trendingUp',
    to: '/reports/wg-trend',
    category: 'Vergleich',
  },

  // ── Spezialisiert ────────────────────────────────────────
  {
    id: 'wochentag-sa',
    title: 'Samstag-Analyse',
    description: 'Nur Samstage über mehrere Monate — wie laufen die starken Tage?',
    icon: 'clock',
    to: '/reports/umsatz',
    preset: { wochentag: [6] },
    category: 'Spezialisiert',
  },
  {
    id: 'wochentag-werktag',
    title: 'Werktag-Analyse (Mo–Fr)',
    description: 'Nur Werktage als Filter — fokussiert auf Arbeitstage.',
    icon: 'clock',
    to: '/reports/umsatz',
    preset: { wochentag: [1, 2, 3, 4, 5] },
    category: 'Spezialisiert',
  },
  {
    id: 'last-7-days',
    title: 'Letzte 7 Tage',
    description: 'Schneller Blick auf die zurückliegende Woche.',
    icon: 'clock',
    to: '/reports/umsatz',
    preset: { datum: lastNDays(7) },
    category: 'Spezialisiert',
  },
  {
    id: 'last-30-days',
    title: 'Letzte 30 Tage',
    description: 'Vollständige Monatsübersicht.',
    icon: 'clock',
    to: '/reports/umsatz',
    preset: { datum: lastNDays(30) },
    category: 'Spezialisiert',
  },
  {
    id: 'filial-benchmark',
    title: 'Filial-Benchmark',
    description: 'Eine Filiale vs. Durchschnitt aller — Vergleich pro Warengruppe.',
    icon: 'trendingUp',
    to: '/reports/benchmark',
    category: 'Spezialisiert',
  },
  {
    id: 'aktive-maerkte',
    title: 'Aktive Märkte',
    description: 'Welche Filialen pushen aktuell — Lücken-Übersicht.',
    icon: 'dashboard',
    to: '/reports/aktivitaet',
    category: 'Spezialisiert',
  },
];

const CATEGORIES: ReportCard['category'][] = [
  'Standard',
  'Vergleich',
  'Tabellen',
  'Spezialisiert',
];

// Pro Kategorie: Icon + kurzer Erklärtext (Orientierung) + Akzentfarbe.
const CATEGORY_META: Record<
  ReportCard['category'],
  { icon: IconName; hint: string }
> = {
  Standard: { icon: 'trendingUp', hint: 'Die wichtigsten Auswertungen für den Alltag.' },
  Vergleich: { icon: 'trendingUp', hint: 'Zeiträume und Perioden gegenüberstellen.' },
  Tabellen: { icon: 'table', hint: 'Rankings & Pivots — exportierbar für Excel/PDF.' },
  Spezialisiert: { icon: 'clock', hint: 'Fokus-Analysen für bestimmte Fragen.' },
};

export function ReportsPage() {
  const [q, setQ] = useState('');
  const query = q.trim().toLowerCase();
  const match = (r: ReportCard) =>
    !query ||
    r.title.toLowerCase().includes(query) ||
    r.description.toLowerCase().includes(query);
  const total = REPORTS.length;
  const shown = REPORTS.filter(match).length;

  return (
    <div className="space-y-6 w-full max-w-[1600px] mx-auto">
      {/* Hero / Einstieg */}
      <div className="card-elevated overflow-hidden">
        <div className="relative px-6 py-6 sm:px-8 sm:py-7 bg-gradient-to-br from-brand-600 to-brand-800 text-white">
          <div className="absolute inset-0 opacity-20 pointer-events-none"
               style={{ background: 'radial-gradient(600px 200px at 0% -40%, #fff, transparent 60%)' }} />
          <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center backdrop-blur">
                  <Icon name="reports" size={20} />
                </div>
                <h2 className="text-2xl font-bold tracking-tight">Berichte &amp; Auswertungen</h2>
              </div>
              <p className="text-sm text-brand-50/90 mt-2 max-w-xl">
                Wähle einen fertigen Bericht — Filter werden automatisch passend gesetzt.
                Filialen, Zeitraum & Warengruppen verfeinerst du danach links im Filter.
              </p>
            </div>
            <div className="relative w-full sm:w-72 shrink-0">
              <Icon name="search" size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/70 pointer-events-none" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Bericht suchen …"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-white/15 backdrop-blur placeholder:text-white/60 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/40 transition"
              />
            </div>
          </div>
        </div>
      </div>

      {query && (
        <p className="text-xs text-slate-500 dark:text-slate-400 -mt-2">
          {shown} von {total} Berichten passen zu „{q}".
        </p>
      )}

      {CATEGORIES.map((cat) => {
        const items = REPORTS.filter((r) => r.category === cat && match(r));
        if (items.length === 0) return null;
        const meta = CATEGORY_META[cat];
        return (
          <section key={cat} className="space-y-3">
            <div className="flex items-center gap-2.5 border-b border-slate-200/70 dark:border-slate-800/70 pb-2">
              <span className="w-7 h-7 rounded-lg bg-brand-50 dark:bg-brand-950 text-brand-600 dark:text-brand-400 flex items-center justify-center shrink-0">
                <Icon name={meta.icon} size={15} />
              </span>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{cat}</h3>
              <span className="badge text-2xs">{items.length}</span>
              <span className="hidden md:block text-xs text-slate-400 dark:text-slate-500 ml-1 truncate">
                {meta.hint}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {items.map((r) => (
                <ReportCardItem key={r.id} report={r} />
              ))}
            </div>
          </section>
        );
      })}

      {shown === 0 && (
        <div className="text-center py-16 text-slate-400 dark:text-slate-500">
          <Icon name="search" size={28} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">Kein Bericht passt zu „{q}".</p>
        </div>
      )}
    </div>
  );
}

function ReportCardItem({ report }: { report: ReportCard }) {
  const navigate = useNavigate();
  const setAll = useFilterStore((s) => s.setAll);
  const clearAll = useFilterStore((s) => s.clearAll);

  function handleClick() {
    if (report.comingSoon) return;
    // Filter setzen oder zurücksetzen
    if (report.preset) {
      setAll(report.preset as never);
    } else if (!report.to.includes('?')) {
      // Keine Query-Params → vermutlich gewollter Reset
      clearAll();
    }
    // Navigation (mit ggf. Query-Params)
    navigate(report.to);
  }

  if (report.comingSoon) {
    return (
      <div className="card opacity-50 cursor-not-allowed">
        <div className="card-body space-y-3">
          <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-400 flex items-center justify-center">
            <Icon name={report.icon} size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              {report.title}
              <span className="badge text-2xs">demnächst</span>
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {report.description}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={handleClick}
      className="card hover:shadow-elevated transition-all hover:-translate-y-0.5 duration-200 text-left group cursor-pointer w-full"
    >
      <div className="card-body space-y-3">
        <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-brand-950 text-brand-600 dark:text-brand-400 flex items-center justify-center group-hover:bg-brand-100 dark:group-hover:bg-brand-900 transition-colors">
          <Icon name={report.icon} size={20} />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
            {report.title}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {report.description}
          </p>
        </div>
      </div>
    </button>
  );
}
